import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, AlertTriangle, AlertCircle, Calendar, User, Building, Users, Link2, FolderTree } from 'lucide-react';
import { api } from '../api/client';
import EntityPicker, { ENTITY_COLORS } from './EntityPicker';
import WBSNodeSelector from './WBSNodeSelector';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const LIKELIHOODS = ['rare', 'unlikely', 'possible', 'likely', 'almost_certain'];
const STATUSES = ['open', 'in_progress', 'closed', 'mitigated'];
const CATEGORIES = ['business', 'project'];
const ESCALATION_LEVELS = ['Project', 'Program', 'Portfolio', 'Executive', 'SteerCo'];

/**
 * Issue/Risk Create/Edit Drawer
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - issue: object | null - Existing issue to edit (null for create)
 * - itemType: 'issue' | 'risk' - Type of item being created
 * - defaultProjectId: string | null - Pre-fill project
 * - defaultTeamId: string | null - Pre-fill team
 * - onSuccess: () => void - Callback after successful save
 */
const IssueDrawer = ({ 
  isOpen, 
  onClose, 
  issue = null, 
  itemType = 'issue',
  defaultProjectId = null,
  defaultTeamId = null,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    item_type: itemType,
    title: '',
    description: '',
    category: 'project',
    priority: 'medium',
    likelihood: 'possible',
    risk_score: 0,
    status: 'open',
    raised_date: new Date().toISOString().split('T')[0],
    due_date: '',
    owner: '',
    team_id: defaultTeamId || '',
    project_id: defaultProjectId || '',
    escalation_level: 'Project',
    resolution: '',
    wbs_id: null,  // WBS Node reference
    links: []
  });
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);

  // Load form data when editing
  useEffect(() => {
    if (issue) {
      setForm({
        item_type: issue.item_type || itemType,
        title: issue.title || '',
        description: issue.description || '',
        category: issue.type?.toLowerCase() || 'project',
        priority: issue.priority?.toLowerCase() || 'medium',
        likelihood: issue.likelihood?.toLowerCase() || 'possible',
        risk_score: issue.risk_score || 0,
        status: issue.status?.toLowerCase() || 'open',
        raised_date: issue.raised_date?.split('T')[0] || '',
        due_date: issue.due_date?.split('T')[0] || '',
        owner: issue.owner || '',
        team_id: issue.team_id || '',
        project_id: issue.project_id || '',
        escalation_level: issue.escalation_level || 'Project',
        resolution: issue.resolution || '',
        wbs_id: issue.wbs_id || null,  // WBS Node reference
        links: issue.links || []
      });
    } else {
      setForm({
        item_type: itemType,
        title: '',
        description: '',
        category: 'project',
        priority: 'medium',
        likelihood: 'possible',
        risk_score: 0,
        status: 'open',
        raised_date: new Date().toISOString().split('T')[0],
        due_date: '',
        owner: '',
        team_id: defaultTeamId || '',
        project_id: defaultProjectId || '',
        escalation_level: 'Project',
        resolution: '',
        wbs_id: null,  // WBS Node reference
        links: []
      });
    }
  }, [issue, itemType, defaultProjectId, defaultTeamId]);

  // Load projects and teams for dropdowns
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, teamsRes] = await Promise.all([
          api.getProjects(),
          api.getTeams()
        ]);
        setProjects(projectsRes.data?.projects || []);
        setTeams(teamsRes.data?.teams || []);
      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      }
    };
    if (isOpen) loadData();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (issue?.id) {
        await api.updateIssue(issue.id, form);
      } else {
        await api.createIssue(form);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!issue?.id) return;
    if (!window.confirm(`Are you sure you want to delete this ${form.item_type}?`)) return;

    setDeleting(true);
    try {
      await api.deleteIssue(issue.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate risk score when likelihood or priority change (for risks)
  useEffect(() => {
    if (form.item_type === 'risk') {
      const priorityScore = { low: 1, medium: 2, high: 3, critical: 4 }[form.priority] || 2;
      const likelihoodScore = { rare: 1, unlikely: 2, possible: 3, likely: 4, almost_certain: 5 }[form.likelihood] || 3;
      setForm(f => ({ ...f, risk_score: priorityScore * likelihoodScore }));
    }
  }, [form.priority, form.likelihood, form.item_type]);

  if (!isOpen) return null;

  const isRisk = form.item_type === 'risk';
  const title = issue?.id 
    ? `Edit ${isRisk ? 'Risk' : 'Issue'}` 
    : `New ${isRisk ? 'Risk' : 'Issue'}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      
      {/* Drawer */}
      <div className="relative w-full max-w-xl bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 border-b border-[var(--clr-border-light)] flex items-center justify-between ${isRisk ? 'bg-amber-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3">
            {isRisk ? (
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-red-600" />
            )}
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">{title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Item Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, item_type: 'issue' })}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.item_type === 'issue' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-[var(--clr-text-secondary)] hover:bg-gray-200'
                }`}
              >
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Issue
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, item_type: 'risk' })}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.item_type === 'risk' 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-gray-100 text-[var(--clr-text-secondary)] hover:bg-gray-200'
                }`}
              >
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Risk
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder={`Enter ${isRisk ? 'risk' : 'issue'} title`}
              data-testid="issue-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder="Describe the issue or risk..."
            />
          </div>

          {/* Category & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c} className="capitalize">{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {PRIORITIES.map(p => (
                  <option key={p} value={p} className="capitalize">{p}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Risk-specific fields */}
          {isRisk && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Likelihood</label>
                <select
                  value={form.likelihood}
                  onChange={(e) => setForm({ ...form, likelihood: e.target.value })}
                  className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
                >
                  {LIKELIHOODS.map(l => (
                    <option key={l} value={l} className="capitalize">{l.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Risk Score</label>
                <input
                  type="number"
                  value={form.risk_score}
                  readOnly
                  className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg bg-gray-50"
                />
              </div>
            </div>
          )}

          {/* Status & Escalation */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Escalation Level</label>
              <select
                value={form.escalation_level}
                onChange={(e) => setForm({ ...form, escalation_level: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                {ESCALATION_LEVELS.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Raised Date
              </label>
              <input
                type="date"
                value={form.raised_date}
                onChange={(e) => setForm({ ...form, raised_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Owner
            </label>
            <input
              type="text"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              placeholder="Owner name"
            />
          </div>

          {/* Project & Team */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Building className="w-4 h-4 inline mr-1" />
                Project
              </label>
              <select
                value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                <option value="">Select Project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Team
              </label>
              <select
                value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                <option value="">Select Team</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* WBS Node Selector */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <FolderTree className="w-4 h-4 inline mr-1" />
              WBS Location
            </label>
            <WBSNodeSelector
              value={form.wbs_id}
              onChange={(wbsId) => setForm({ ...form, wbs_id: wbsId })}
              placeholder="Select WBS node"
              allowClear={true}
              data-testid="issue-wbs-selector"
            />
            <p className="text-xs text-gray-500 mt-1">
              Link this {isRisk ? 'risk' : 'issue'} to a Work Breakdown Structure node
            </p>
          </div>

          {/* Linked Entities */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <Link2 className="w-4 h-4 inline mr-1" />
              Linked Entities
            </label>
            <EntityPicker
              multi={true}
              value={form.links}
              onChange={(links) => setForm({ ...form, links })}
              placeholder="Search and link to portfolios, programs, epics, vendors..."
            />
          </div>

          {/* Resolution (for closed issues) */}
          {form.status === 'closed' && (
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Resolution</label>
              <textarea
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                placeholder="How was this resolved?"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[var(--clr-border-light)]">
            {issue?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <div className={`flex gap-2 ${!issue?.id ? 'ml-auto' : ''}`}>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                data-testid="save-issue-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueDrawer;
